import { Alert, Grid2, Stack, Typography } from '@mui/material'
import { Dispatch, useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CRITERIA_TYPE_TYPE } from 'src/shared/constants/assessment-constants'
import { CONFIGURATION_STATUS_TYPE, PROCESS_TYPE, TEXT_LENGTH } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { RadioButtonGroupField, RadioData } from 'src/shared/form/control/RadioButtonGroupField'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import {
  getCriteriaConfigProcessOptions,
  getCriteriaTypeOptions,
} from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { usePrompt } from 'src/routing/routing-hooks'
import { createDecorators } from 'src/shared/form/utils/decorators'

export interface CriteriaGroupFormValues {
  enName: string
  deName: string
  frName: string
  itName: string
  status: CONFIGURATION_STATUS_TYPE
  processAlloc: PROCESS_TYPE
  criteriaTypes: CRITERIA_TYPE_TYPE[]
}

interface CriteriaConfigurationCriteriaGroupsFormProps {
  mode: 'EDIT' | 'CREATE'
  onSubmit: (values: CriteriaGroupFormValues, setFormDirty: Dispatch<boolean>) => Promise<void>
  initialValues: CriteriaGroupFormValues
}

export const CriteriaConfigurationCriteriaGroupsForm = ({
  mode,
  onSubmit,
  initialValues,
}: CriteriaConfigurationCriteriaGroupsFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()

  const [formDirty, setFormDirty] = useState<boolean>(false)

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])
  const processAllocOptions: RadioData[] = useMemo(() => getCriteriaConfigProcessOptions(getMessage), [getMessage])
  const criteriaTypeOptions: Option[] = useMemo(() => getCriteriaTypeOptions(getMessage), [getMessage])

  usePrompt(getMessage('notification.form.unsaved.changes'), formDirty)

  return (
    <Stack spacing={2}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<CriteriaGroupFormValues>
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values, setFormDirty)}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="criteria-group-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable={true}
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Stack spacing={3}>
                  <Typography variant="h2">{getMessage('label.title')}</Typography>
                  <HtmlEditorField
                    name="deName"
                    label={getMessage('label.configuration.criteria.group.edit.name.de')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={composeValidators(maxChar(TEXT_LENGTH.XL), required())}
                    required
                  />
                  <HtmlEditorField
                    name="enName"
                    label={getMessage('label.configuration.criteria.group.edit.name.en')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={composeValidators(maxChar(TEXT_LENGTH.XL), required())}
                    required
                  />
                  <HtmlEditorField
                    name="frName"
                    label={getMessage('label.configuration.criteria.group.edit.name.fr')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={composeValidators(maxChar(TEXT_LENGTH.XL), required())}
                    required
                  />
                  <HtmlEditorField
                    name="itName"
                    label={getMessage('label.configuration.criteria.group.edit.name.it')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={composeValidators(maxChar(TEXT_LENGTH.XL), required())}
                    required
                  />
                </Stack>

                <Typography variant="h2">{getMessage('label.configuration.grid.allocation')}</Typography>

                <Grid2 container columnSpacing={5} rowSpacing={3}>
                  <CheckboxGroupField
                    disabled={mode === 'EDIT'}
                    name="criteriaTypes"
                    color="primary"
                    required
                    label={
                      <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                        {getMessage('label.criteria_type')}
                      </Typography>
                    }
                    validate={required()}
                    data={criteriaTypeOptions}
                  />

                  <RadioButtonGroupField
                    disabled={mode === 'EDIT'}
                    name="processAlloc"
                    color="primary"
                    required
                    label={
                      <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                        {getMessage('label.process')}
                      </Typography>
                    }
                    data={processAllocOptions}
                  />
                </Grid2>
              </Stack>

              <FormSpy
                subscription={{ dirty: true }}
                onChange={(props: { dirty: boolean }) => {
                  setFormDirty(props.dirty)
                }}
              />
            </form>
          )
        }}
      />
    </Stack>
  )
}
